/**
 * WordToJsonConverter Service
 * Convert Word (.docx) files to JSON format (ContentItem structure)
 */

import { ContentItem } from '../../types'
import { imageExtractor } from './ImageExtractor'

// Lazy load mammoth - try multiple strategies
let mammothLib: any = null

async function loadMammoth(): Promise<any> {
  if (mammothLib) {
    return mammothLib
  }

  // Strategy 1: Try direct import (Vite should bundle this)
  try {
    console.log('📦 Loading mammoth (strategy 1: npm module)...')
    const module = await import('mammoth')
    mammothLib = module.default || module
    if (mammothLib && mammothLib.convertToHtml) {
      console.log('✅ Mammoth loaded from npm module')
      return mammothLib
    }
  } catch (err1: any) {
    console.warn('⚠️ Strategy 1 failed:', err1.message)
  }

  // Strategy 2: Try loading from CDN as fallback
  try {
    console.log('📦 Loading mammoth (strategy 2: CDN fallback)...')

    // Load mammoth from CDN
    const script = document.createElement('script')
    script.src = 'https://cdn.jsdelivr.net/npm/mammoth@1.11.0/mammoth.browser.min.js'
    script.type = 'text/javascript'

    await new Promise((resolve, reject) => {
      script.onload = () => {
        // @ts-ignore - mammoth will be available on window after CDN load
        const windowMammoth = (window as any).mammoth
        if (windowMammoth) {
          mammothLib = windowMammoth
          console.log('✅ Mammoth loaded from CDN')
          resolve(mammothLib)
        } else {
          reject(new Error('mammoth not found on window after CDN load'))
        }
      }
      script.onerror = () => reject(new Error('Failed to load mammoth from CDN'))
      if (typeof document !== 'undefined') {
        document.head.appendChild(script)
      } else {
        reject(new Error('document is not available'))
      }
    })

    if (mammothLib && mammothLib.convertToHtml) {
      return mammothLib
    }
  } catch (err2: any) {
    console.warn('⚠️ Strategy 2 failed:', err2.message)
  }

  // All strategies failed
  throw new Error(
    'Cannot load mammoth library. Please:\n' +
    '1. Ensure mammoth is installed: npm install mammoth\n' +
    '2. Restart dev server: npm run dev\n' +
    '3. Clear Vite cache: Remove-Item -Recurse -Force node_modules\.vite\n' +
    '4. Check internet connection (for CDN fallback)'
  )
}

export interface LessonData {
  id: number
  tieuDe: string
  moTa: string
  metadata: {
    thoiGian: number
    doKho: string
    chuDe: string[]
    mucTieu: string[]
  }
  noiDung: {
    phanMoDau: {
      tieuDe: string
      noiDung: ContentItem[]
    }
    phanNoiDung: {
      tieuDe: string
      noiDung: ContentItem[]
    }
    phanCuoi: {
      tieuDe: string
      noiDung: ContentItem[]
    }
  }
}

class WordToJsonConverter {
  /**
   * Convert Word file to JSON (LessonData structure)
   */
  async convertWordToJson(
    wordFile: File,
    teacherId: string,
    lessonId: string
  ): Promise<LessonData> {
    try {
      console.log(`📄 Converting Word file: ${wordFile.name}`)

      // Load mammoth using lazy loader
      console.log('📦 Loading mammoth library...')
      const mammoth = await loadMammoth()
      console.log('✅ Mammoth loaded successfully')

      // Read Word file as array buffer
      const arrayBuffer = await wordFile.arrayBuffer()

      // Convert to HTML using mammoth
      const mammothAny = mammoth as any
      const convertImageHandler =
        mammothAny?.images?.inline?.((element: any) => this.handleEmbeddedImageElement(element, teacherId, lessonId))

      const result = await mammoth.convertToHtml(
        { arrayBuffer },
        {
          styleMap: [
            "p[style-name='Heading 1'] => h1:fresh",
            "p[style-name='Heading 2'] => h2:fresh",
            "p[style-name='Heading 3'] => h3:fresh",
            "p[style-name='List Paragraph'] => p:fresh"
          ],
          convertImage: convertImageHandler
        }
      )

      const html = result.value
      const messages = result.messages

      if (messages.length > 0) {
        console.warn('⚠️ Mammoth conversion warnings:', messages)
      }

      // Extract text content
      const textContent = this.htmlToText(html)

      // Also extract from HTML directly (may contain <img> tags or links)
      const htmlText = html + ' ' + textContent

      // Extract image/video links from both HTML and text
      const mediaLinks = imageExtractor.extractMediaLinks(htmlText)
      console.log(`📸 Found ${mediaLinks.length} media links`)

      if (mediaLinks.length > 0) {
        console.log('📸 Media links:', mediaLinks)
      }

      // Download and save media files
      const urlMap = new Map<string, string>()
      if (mediaLinks.length > 0) {
        console.log('🔄 [WORD CONVERT] Starting download of', mediaLinks.length, 'media files...')
        const localPaths = await imageExtractor.downloadAndSaveMultipleMedia(
          mediaLinks,
          teacherId,
          lessonId
        )
        console.log('✅ [WORD CONVERT] Download completed, mapping URLs to local paths...')
        mediaLinks.forEach((url, index) => {
          urlMap.set(url, localPaths[index])
          console.log(`   📌 ${url} → ${localPaths[index]}`)
        })
      } else {
        console.log('ℹ️ [WORD CONVERT] No media links found in document')
      }

      // Replace URLs in HTML with local paths
      let processedHtml = html
      urlMap.forEach((localPath, originalUrl) => {
        processedHtml = processedHtml.replace(
          new RegExp(escapeRegex(originalUrl), 'g'),
          localPath
        )
      })

      // Convert HTML to ContentItem structure
      const contentItems = this.htmlToContentItems(processedHtml)

      // Parse document structure
      const parsedData = this.parseDocumentStructure(contentItems)

      // Create LessonData
      const lessonData: LessonData = {
        id: Date.now(), // Temporary ID
        tieuDe: parsedData.title || 'Bài học',
        moTa: parsedData.description || '',
        metadata: {
          thoiGian: parsedData.thoiGian || 45,
          doKho: parsedData.doKho || 'trung bình',
          chuDe: parsedData.chuDe || [],
          mucTieu: parsedData.mucTieu || []
        },
        noiDung: {
          phanMoDau: {
            tieuDe: parsedData.phanMoDau?.tieuDe || 'Phần mở đầu',
            noiDung: parsedData.phanMoDau?.noiDung || []
          },
          phanNoiDung: {
            tieuDe: parsedData.phanNoiDung?.tieuDe || 'Phần nội dung',
            noiDung: parsedData.phanNoiDung?.noiDung || []
          },
          phanCuoi: {
            tieuDe: parsedData.phanCuoi?.tieuDe || 'Phần cuối',
            noiDung: parsedData.phanCuoi?.noiDung || []
          }
        }
      }

      console.log(`✅ Word converted successfully`)
      return lessonData

    } catch (error) {
      console.error('❌ Error converting Word to JSON:', error)
      throw new Error(`Failed to convert Word file: ${error instanceof Error ? error.message : 'Unknown error'}`)
    }
  }

  private async handleEmbeddedImageElement(element: any, teacherId: string, lessonId: string) {
    try {
      const base64 = await element.read('base64')
      const contentType = element.contentType || 'image/png'
      const localPath = await imageExtractor.saveEmbeddedMediaFromBase64(
        base64,
        contentType,
        teacherId,
        lessonId,
        'image',
        { lessonKey: lessonId, contentTypeParam: 'theory' }
      )
      return {
        src: localPath,
        alt: element.altText || 'Embedded image'
      }
    } catch (error) {
      console.error('❌ Failed to process embedded image:', error)
      try {
        const fallbackBase64 = await element.read('base64')
        return {
          src: `data:${element.contentType || 'image/png'};base64,${fallbackBase64}`,
          alt: element.altText || 'Embedded image'
        }
      } catch (innerError) {
        console.error('❌ Failed to read embedded image for fallback:', innerError)
        return {
          src: '',
          alt: element.altText || 'Embedded image'
        }
      }
    }
  }

  /**
   * Convert HTML to plain text
   */
  private htmlToText(html: string): string {
    if (typeof document === 'undefined') {
      // Server-side: strip HTML tags using regex
      return html.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
    }
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html
    return tempDiv.textContent || tempDiv.innerText || ''
  }

  /**
   * Convert HTML to ContentItem array
   */
  private htmlToContentItems(html: string): ContentItem[] {
    if (typeof document === 'undefined') {
      // Server-side: return empty array
      return []
    }
    const items: ContentItem[] = []
    const tempDiv = document.createElement('div')
    tempDiv.innerHTML = html

    // Process each child node
    Array.from(tempDiv.children).forEach(element => {
      const item = this.elementToContentItem(element)
      if (item) {
        items.push(item)
      }
    })

    // Also process text nodes
    Array.from(tempDiv.childNodes).forEach(node => {
      // Use numeric constant instead of Node.TEXT_NODE to avoid reference errors
      if (node.nodeType === 3 && node.textContent?.trim()) { // 3 = TEXT_NODE
        items.push({
          type: 'text',
          content: node.textContent.trim()
        })
      }
    })

    return items
  }

  /**
   * Convert HTML element to ContentItem
   */
  private elementToContentItem(element: Element): ContentItem | null {
    const tagName = element.tagName.toLowerCase()
    const textContent = element.textContent?.trim() || ''

    switch (tagName) {
      case 'h1':
        return {
          type: 'heading',
          content: textContent,
          level: 1
        }

      case 'h2':
        return {
          type: 'heading',
          content: textContent,
          level: 2
        }

      case 'h3':
        return {
          type: 'heading',
          content: textContent,
          level: 3
        }

      case 'p':
        // Check if contains image
        const img = element.querySelector('img')
        if (img) {
          return {
            type: 'image',
            content: img.getAttribute('src') || '',
            alt: img.getAttribute('alt') || '',
            caption: textContent
          }
        }

        // Check if contains video
        const video = element.querySelector('video')
        if (video) {
          return {
            type: 'video',
            content: video.getAttribute('src') || ''
          }
        }

        // Check if contains LaTeX formula
        const latexMatch = this.detectLaTeX(textContent)
        if (latexMatch) {
          return latexMatch
        }

        // Auto-detect heading based on keywords
        const headingMatch = this.detectHeading(textContent)
        if (headingMatch) {
          return headingMatch
        }

        // Auto-detect interactive question
        const questionMatch = this.detectInteractiveQuestion(textContent)
        if (questionMatch) {
          return questionMatch
        }

        // Check if contains image/video URL
        const urlMatch = this.detectMediaUrl(textContent)
        if (urlMatch) {
          return urlMatch
        }

        // Regular paragraph
        if (textContent) {
          return {
            type: 'text',
            content: textContent
          }
        }
        return null

      case 'ul':
      case 'ol':
        const listItems = Array.from(element.querySelectorAll('li'))
          .map(li => {
            const text = li.textContent?.trim() || ''
            // Check if item has * (correct answer marker)
            if (text.startsWith('*')) {
              return {
                text: text.substring(1).trim(),
                isCorrect: true
              }
            }
            return {
              text: text,
              isCorrect: false
            }
          })
          .filter(item => item.text.length > 0)

        // Check if this is an interactive question list
        const hasCorrectAnswer = listItems.some(item => item.isCorrect)
        if (hasCorrectAnswer) {
          // This is likely an interactive question with options
          const options = listItems.map(item => item.text)
          const correctIndex = listItems.findIndex(item => item.isCorrect)

          // Try to find question text from previous elements
          const questionText = this.findQuestionText(element)

          return {
            type: 'interactive_question',
            content: questionText || 'Câu hỏi tương tác',
            question: questionText || 'Câu hỏi tương tác',
            options: options,
            correctAnswer: correctIndex >= 0 ? correctIndex : 0,
            difficulty: 'medium'
          }
        }

        return {
          type: 'list',
          content: listItems.map(item => item.text) as any,
          style: tagName === 'ol' ? 'numbered' : 'bullet',
          items: listItems.map(item => item.text)
        }

      case 'img':
        return {
          type: 'image',
          content: element.getAttribute('src') || '',
          alt: element.getAttribute('alt') || ''
        }

      default:
        // For other elements, extract text
        if (textContent) {
          return {
            type: 'text',
            content: textContent
          }
        }
        return null
    }
  }

  /**
   * Auto-detect heading based on keywords
   */
  private detectHeading(text: string): ContentItem | null {
    const trimmed = text.trim()

    if (trimmed.includes('$')) {
      return null
    }

    // Heading 1: Main title (usually first line or contains "Bài", "Chương")
    // Note: We can't access items.length here, so we'll check in parseDocumentStructure
    if (trimmed.match(/^(Bài|Chương|BÀI|CHƯƠNG)/i)) {
      return {
        type: 'heading',
        content: trimmed,
        level: 1
      }
    }

    // Heading 2: Section headers
    const sectionKeywords = ['Phần Mở Đầu', 'Phần Nội Dung', 'Phần Cuối',
      'PHẦN MỞ ĐẦU', 'PHẦN NỘI DUNG', 'PHẦN CUỐI',
      'Mở Đầu', 'Nội Dung', 'Cuối']
    if (sectionKeywords.some(keyword => trimmed.includes(keyword))) {
      return {
        type: 'heading',
        content: trimmed,
        level: 2
      }
    }

    // Heading 3: Subsection (numbered like "1.", "2.", etc. or contains ":", ".")
    if (trimmed.match(/^\d+[\.\)]\s/) || trimmed.match(/^[IVX]+[\.\)]\s/)) {
      return {
        type: 'heading',
        content: trimmed,
        level: 3
      }
    }

    return null
  }

  /**
   * Auto-detect interactive question
   */
  private detectInteractiveQuestion(text: string): ContentItem | null {
    const trimmed = text.trim()

    // Check if text ends with "?" or contains question keywords
    if (trimmed.endsWith('?') ||
      trimmed.match(/(Câu hỏi|Hỏi|Question|Câu|Vấn đề)[:：]/i)) {
      return {
        type: 'interactive_question',
        content: trimmed,
        question: trimmed,
        options: [],
        correctAnswer: 0,
        difficulty: 'medium'
      }
    }

    return null
  }

  /**
   * Auto-detect LaTeX formula
   */
  private detectLaTeX(text: string): ContentItem | null {
    const trimmed = text.trim()

    const blockOnlyMatch = trimmed.match(/^\s*\$\$([\s\S]+)\$\$\s*$/)
    if (blockOnlyMatch && blockOnlyMatch[1]) {
      return {
        type: 'formula',
        content: blockOnlyMatch[1].trim()
      }
    }

    const inlineOnlyMatch = trimmed.match(/^\s*\$([^$]+)\$\s*$/)
    if (inlineOnlyMatch && inlineOnlyMatch[1]) {
      return {
        type: 'formula',
        content: inlineOnlyMatch[1].trim()
      }
    }

    return null
  }

  /**
   * Auto-detect media URL (image/video)
   */
  private detectMediaUrl(text: string): ContentItem | null {
    const trimmed = text.trim()

    // Check if it's an absolute URL
    const urlPattern = /https?:\/\/[^\s\)]+/
    const urlMatch = trimmed.match(urlPattern)

    if (urlMatch) {
      const url = urlMatch[0]
      const lowerUrl = url.toLowerCase()

      // Check if it's YouTube
      if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
        // Extract video ID
        let videoId = ''
        const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s\)]+)/)
        if (youtubeMatch) {
          videoId = youtubeMatch[1]
        }

        return {
          type: 'video',
          content: url,
          src: url,
          videoId: videoId,
          isYouTube: true
        } as any
      }

      // Check if it's an image
      if (url.match(/\.(jpg|jpeg|png|gif|webp|svg)(\?|$)/i)) {
        return {
          type: 'image',
          content: url,
          src: url,
          alt: trimmed.replace(url, '').trim() || 'Image'
        } as any
      }

      // Check if it's a video file
      if (url.match(/\.(mp4|webm|ogg|mov)(\?|$)/i)) {
        return {
          type: 'video',
          content: url,
          src: url
        } as any
      }

      // Default to image if it's a URL
      return {
        type: 'image',
        content: url,
        src: url,
        alt: trimmed.replace(url, '').trim() || 'Media'
      } as any
    }

    // Check for local file paths
    const localPathPattern = /[A-Z]:\\(?:[^\\/:*?"<>|\r\n]+\\)*[^\\/:*?"<>|\r\n]+\.(jpg|jpeg|png|gif|webp|svg|mp4|webm|ogg|mov)/i
    const localMatch = trimmed.match(localPathPattern)
    if (localMatch) {
      const path = localMatch[0]
      const isVideo = /\.(mp4|webm|ogg|mov)/i.test(path)

      return {
        type: isVideo ? 'video' : 'image',
        content: path,
        src: path,
        alt: trimmed.replace(path, '').trim() || (isVideo ? 'Video' : 'Image')
      } as any
    }

    // Check for internal teacher-content paths
    const internalPathPattern = /(\/)?teacher-content\/[^\s\)]+/i
    const internalMatch = trimmed.match(internalPathPattern)
    if (internalMatch) {
      const path = internalMatch[0].startsWith('/') ? internalMatch[0] : `/${internalMatch[0]}`
      const isVideo = /\.(mp4|webm|ogg|mov)/i.test(path)
      return {
        type: isVideo ? 'video' : 'image',
        content: path,
        src: path,
        alt: trimmed.replace(internalMatch[0], '').trim() || (isVideo ? 'Video' : 'Image')
      } as any
    }

    return null
  }

  /**
   * Find question text from previous elements
   */
  private findQuestionText(element: Element): string {
    // Look for previous paragraph that might be the question
    let prev = element.previousElementSibling
    while (prev) {
      const text = prev.textContent?.trim() || ''
      if (text.endsWith('?') || text.match(/(Câu hỏi|Hỏi|Question)[:：]/i)) {
        return text
      }
      prev = prev.previousElementSibling
    }
    return ''
  }

  /**
   * Parse document structure to extract sections
   */
  private parseDocumentStructure(items: ContentItem[]): {
    title?: string
    description?: string
    thoiGian?: number
    doKho?: string
    chuDe?: string[]
    mucTieu?: string[]
    phanMoDau?: { tieuDe: string; noiDung: ContentItem[] }
    phanNoiDung?: { tieuDe: string; noiDung: ContentItem[] }
    phanCuoi?: { tieuDe: string; noiDung: ContentItem[] }
  } {
    const result: any = {
      phanMoDau: { tieuDe: 'Phần mở đầu', noiDung: [] },
      phanNoiDung: { tieuDe: 'Phần nội dung', noiDung: [] },
      phanCuoi: { tieuDe: 'Phần cuối', noiDung: [] }
    }

    let currentSection = 'phanMoDau'
    let sectionTitle = ''

    items.forEach((item, index) => {
      // Detect section headers
      if (item.type === 'heading' && item.level === 1) {
        const headingText = typeof item.content === 'string' ? item.content : ''

        if (headingText.toLowerCase().includes('mở đầu') ||
          headingText.toLowerCase().includes('giới thiệu')) {
          currentSection = 'phanMoDau'
          sectionTitle = headingText
        } else if (headingText.toLowerCase().includes('nội dung') ||
          headingText.toLowerCase().includes('nội dung chính')) {
          currentSection = 'phanNoiDung'
          sectionTitle = headingText
        } else if (headingText.toLowerCase().includes('cuối') ||
          headingText.toLowerCase().includes('kết luận')) {
          currentSection = 'phanCuoi'
          sectionTitle = headingText
        } else if (index === 0) {
          // First heading is likely the title
          result.title = headingText
          return
        }

        if (sectionTitle) {
          result[currentSection].tieuDe = sectionTitle
        }
      } else {
        // Add to current section
        if (!result[currentSection]) {
          result[currentSection] = { tieuDe: '', noiDung: [] }
        }
        result[currentSection].noiDung.push(item)
      }
    })

    return result
  }
}

// Helper function
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

export const wordToJsonConverter = new WordToJsonConverter()
export default wordToJsonConverter

